import { getInjectedJsonOrFail } from '@/services/get-injected-json';
import { GRAPHICS_CATEGORY_ID } from '@/utilities/consts';
import {
  CATEGORY_EDITORIAL_DEFAULT_SLUG,
  CATEGORY_FPS_DEFAULT_SLUG,
  CATEGORY_PEOPLE_DEFAULT_SLUG,
  freshCategoryFilters,
} from './category-repo';
import { DATE_ADDED_DEFAULT_VALUE, SORT_BY_DEFAULT_VALUE } from './filter-meta';

const { categories } = getInjectedJsonOrFail('browse_category_filters');

function specToString({ specKey, value }) {
  return `${specKey}:${value}`;
}

const getSelectedSlugs = filters => Object.values(filters)
  .filter(filter => filter.selected)
  .map(filter => filter.slug);

const arrayToString = (slugsArray) => {
  let str = '';
  if (slugsArray.length) {
    str = slugsArray.join(',');
  }
  return str;
};

const categoriesToString = ({
  filters, isFree, isVector,
}) => {
  const selectedSlugs = getSelectedSlugs(filters);

  if (isFree) {
    selectedSlugs.push('free');
  }

  if (isVector) {
    selectedSlugs.push('vector');
  }

  return arrayToString(selectedSlugs);
};

function categoriesToSpecString(category) {
  const segments = [];

  const baseCategory = freshCategoryFilters(category.slug);

  if (
    baseCategory.hasVersions
    && category.version?.length > 0
  ) {
    const versions = specToString({
      specKey: 'version',
      value: category.version.join('|'),
    });

    segments.push(versions);
  }

  if (
    baseCategory.hasResolutions
    && category.resolution?.length > 0
  ) {
    const resolutions = specToString({
      specKey: 'resolution',
      value: category.resolution.join('|'),
    });
    segments.push(resolutions);
  }

  if (
    (baseCategory.hasResolutions || category.id === GRAPHICS_CATEGORY_ID)
    && category.orientation?.length > 0
  ) {
    const orientations = specToString({
      specKey: 'orientation',
      value: category.orientation.join('|'),
    });
    segments.push(orientations);
  }

  if (
    baseCategory.hasFps
    && category.fps
    && category.fps.slug !== CATEGORY_FPS_DEFAULT_SLUG
  ) {
    const fps = specToString({
      specKey: 'fps',
      value: category.fps.slug,
    });
    segments.push(fps);
  }

  if (baseCategory.hasBpms) {
    if (category.bpms && category.bpms.length) {
      const bpms = specToString({
        specKey: 'bpms',
        value: category.bpms.join('|'),
      });
      segments.push(bpms);
    }

    if (category.durations && category.durations.length) {
      const durations = specToString({
        categorySlug: category.slug,
        specKey: 'durations',
        value: category.durations.join('|'),
      });
      segments.push(durations);
    }

    if (category.proAffiliated) {
      const proAffiliated = specToString({
        specKey: 'proaffiliated',
        value: category.proAffiliated === -1 ? 'exclude' : 'only',
      });
      segments.push(proAffiliated);
    }
  }

  if (baseCategory.hasRightsManagers) {
    if (category.rightsManager) {
      const rightsManager = specToString({
        specKey: 'rightsManager',
        value: category.rightsManager === -1 ? 'exclude' : 'only',
      });
      segments.push(rightsManager);
    }
  }

  if (baseCategory.hasEditorials) {
    if (
      category.editorial
      && category.editorial.slug !== CATEGORY_EDITORIAL_DEFAULT_SLUG
    ) {
      const editorial = specToString({
        specKey: 'editorial',
        value: category.editorial.slug,
      });

      segments.push(editorial);
    }
  }

  if (
    baseCategory.id === GRAPHICS_CATEGORY_ID
    && category.people
    && category.people.slug !== CATEGORY_PEOPLE_DEFAULT_SLUG
  ) {
    const people = specToString({
      specKey: 'people',
      value: category.people.slug,
    });

    segments.push(people);
  }

  return segments.join(',');
}

export default function stateToUrl({
  dateAdded, sortBy, isFree, categoryFilters, searchTags = [], productsCurrentPage,
}) {
  const query = {};

  if (searchTags.length) {
    query.q = searchTags.join(',');
  }

  if (dateAdded !== DATE_ADDED_DEFAULT_VALUE) {
    query.date_added = dateAdded;
  }

  if (sortBy !== SORT_BY_DEFAULT_VALUE) {
    query.sort_by = sortBy;
  }

  let baseUrl = `/${categoryFilters.slug}`;

  let selectedSubCategories = Object.values(categoryFilters.subCategories)
    .filter(sc => sc.selected);

  let pathOccupiedBySubCategory = false;
  if (isFree) {
    baseUrl += '/free';
    pathOccupiedBySubCategory = true;
  } else {
    const selectedSubCategorySlugs = selectedSubCategories.map(item => item.slug);
    categories.forEach((category) => {
      if (categoryFilters.slug === category.slug) {
        category.sub_categories.every((subcategory) => {
          if (selectedSubCategorySlugs.includes(subcategory.slug)) {
            baseUrl += `/${subcategory.slug}`;
            selectedSubCategories = selectedSubCategories.filter(item => item.slug !== subcategory.slug);
            pathOccupiedBySubCategory = true;
            return false;
          }
          return true;
        });
      }
    });
  }

  if (categoryFilters.templateDifficultyLevel) {
    if (!pathOccupiedBySubCategory) {
      baseUrl += '/easy-to-use';
    }
    query.template_difficulty_level = categoryFilters.templateDifficultyLevel;
  }

  const subcategories = categoriesToString({ filters: selectedSubCategories });
  if (subcategories) {
    query.subcategories = subcategories;
  }

  if (categoryFilters.id === GRAPHICS_CATEGORY_ID) {
    const fileTypes = categoriesToString({
      filters: categoryFilters.fileTypes,
      isVector: categoryFilters.isVector,
    });
    const applicationsSupported = categoriesToString({ filters: categoryFilters.applicationsSupported });
    const themes = categoriesToString({ filters: categoryFilters.themes });
    const artStyles = categoriesToString({ filters: categoryFilters.artStyles });

    if (fileTypes) {
      query.filetypes = fileTypes;
    }

    if (applicationsSupported) {
      query.applicationsSupported = applicationsSupported;
    }

    if (themes) {
      query.themes = themes;
    }

    if (artStyles) {
      query.artstyles = artStyles;
    }

    if (categoryFilters.isIsolated) {
      query.isolated = true;
    }
  }

  const specs = categoriesToSpecString(categoryFilters);

  if (specs) {
    query.specs = specs;
  }

  if (productsCurrentPage > 1) {
    query.page = productsCurrentPage;
  }

  return `${baseUrl}/?${Object.keys(query)
    .map((key) => {
      const value = query[key];
      return `${key}=${value}`;
    })
    .join('&')}`;
}
